import React, { ReactNode, ReactElement, ComponentPropsWithRef, createContext, useContext } from 'react';
import Fieldset from '../Fieldset';
import controlHintIds from '../controlHintIds';
import FormGroup from '../FormGroup';
import Legend from '../Legend';
import Hint from '../Hint';
import ErrorMessage from '../ErrorMessage';
import Select from '../Select';
import Label from '../Label';
import Input from '../Input';
import { getGlobalFormConfig, useFormConfig } from '../FormConfigContext';

const MemorableDateContext = createContext<{
  hintId?: string;
  errorId?: string;
}>({});

type MonthInputProps = Omit<ComponentPropsWithRef<typeof Select>, 'id'> & {
  label?: string;
  id?: string;
};

const DefaultMonthOptions = (
  <>
    <option value="">- Select -</option>
    <option value="1">01 - January</option>
    <option value="2">02 - February</option>
    <option value="3">03 - March</option>
    <option value="4">04 - April</option>
    <option value="5">05 - May</option>
    <option value="6">06 - June</option>
    <option value="7">07 - July</option>
    <option value="8">08 - August</option>
    <option value="9">09 - September</option>
    <option value="10">10 - October</option>
    <option value="11">11 - November</option>
    <option value="12">12 - December</option>
  </>
);

function MonthInput({
  'aria-describedby': ariaDescribedBy,
  label = 'Month',
  name = 'date_of_birth_month',
  children,
  ...rest
}: MonthInputProps) {
  const { hintId, errorId } = useContext(MemorableDateContext);
  return (
    <FormGroup className="usa-form-group--month usa-form-group--select">
      <Label htmlFor={name}>{label}</Label>
      <Select
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...rest}
        id={name}
        className="memorable-date-month__select"
        name={name}
        aria-describedby={[ariaDescribedBy, hintId, errorId].filter(Boolean).join(' ') || undefined}
        errorId={errorId}
      >
        {children || DefaultMonthOptions}
      </Select>
    </FormGroup>
  );
}

type DayInputProps = Omit<ComponentPropsWithRef<typeof Input>, 'id'> & {
  label?: string;
  id?: string;
};

function DayInput({
  label = 'Day',
  'aria-describedby': ariaDescribedBy,
  name = 'date_of_birth_day',
  ...rest
}: DayInputProps) {
  const { hintId, errorId } = useContext(MemorableDateContext);
  return (
    <FormGroup className="usa-form-group--day">
      <Label htmlFor={name}>{label}</Label>
      <Input
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...rest}
        id={name}
        className="memorable-date-day__input"
        name={name}
        maxLength={2}
        pattern="[0-9]*"
        inputMode="numeric"
        aria-describedby={[ariaDescribedBy, hintId, errorId].filter(Boolean).join(' ') || undefined}
        errorId={errorId}
      />
    </FormGroup>
  );
}

type YearInputProps = Omit<ComponentPropsWithRef<typeof Input>, 'id'> & {
  label?: string;
  id?: string;
};

function YearInput({
  label = 'Year',
  'aria-describedby': ariaDescribedBy,
  name = 'date_of_birth_year',
  ...rest
}: YearInputProps) {
  const { hintId, errorId } = useContext(MemorableDateContext);
  return (
    <FormGroup className="usa-form-group--year">
      <Label htmlFor={name}>{label}</Label>
      <Input
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...rest}
        id={name}
        className="memorable-date-year__input"
        name={name}
        minLength={4}
        maxLength={4}
        pattern="[0-9]*"
        aria-describedby={[ariaDescribedBy, hintId, errorId].filter(Boolean).join(' ') || undefined}
        errorId={errorId}
      />
    </FormGroup>
  );
}

export interface MemorableDateProps {
  id: string;
  label: ReactNode;
  children:
    | [
        ReactElement<ComponentPropsWithRef<typeof MonthInput>>,
        ReactElement<ComponentPropsWithRef<typeof DayInput>>,
        ReactElement<ComponentPropsWithRef<typeof YearInput>>,
      ]
    | [
        ReactElement<ComponentPropsWithRef<typeof DayInput>>,
        ReactElement<ComponentPropsWithRef<typeof MonthInput>>,
        ReactElement<ComponentPropsWithRef<typeof YearInput>>,
      ];
  errorMessage?: string;
  hint?: string;
  labelHint?: ReactNode;
  className?: string;
  required?: boolean;
  requiredLabel?: ReactNode;
}

/**
 * The MemorableDate Control based on [The USWDS Memorable Date component](https://designsystem.digital.gov/components/memorable-date/)
 *
 * note: Configured `requiredLabel` text is appended to the labelhint if required attribute is true.
 */
const MemorableDate = ({
  id,
  className,
  label,
  labelHint,
  errorMessage,
  hint,
  children,
  required,
  requiredLabel,
}: MemorableDateProps): JSX.Element => {
  const { labelHintId, hintId, errorId } = controlHintIds(id, labelHint, hint, errorMessage);
  const { requiredLabel: contextRequiredLabel } = useFormConfig();
  const { requiredLabel: globalRequiredLabel } = getGlobalFormConfig();
  const finalRequiredLabel = requiredLabel || contextRequiredLabel || globalRequiredLabel;
  return (
    <FormGroup error={errorId && errorMessage}>
      <Fieldset className={className}>
        <Legend>
          {label}
          {required ? <> {finalRequiredLabel}</> : null}
          {labelHint ? (
            <>
              {' '}
              <Hint id={labelHintId}>{labelHint}</Hint>
            </>
          ) : null}
        </Legend>
        {hint && hintId ? <Hint id={hintId}>{hint}</Hint> : null}
        {errorId && errorMessage ? <ErrorMessage id={errorId}>{errorMessage}</ErrorMessage> : null}
        <div id={id} className="usa-memorable-date">
          <MemorableDateContext.Provider
            value={{
              hintId,
              errorId,
            }}
          >
            {children}
          </MemorableDateContext.Provider>
        </div>
      </Fieldset>
    </FormGroup>
  );
};

MemorableDate.Month = MonthInput;
MemorableDate.Day = DayInput;
MemorableDate.Year = YearInput;

export default MemorableDate;
